import {
    Box,
    Typography,
    Button,
    Paper,
    Avatar,
    Divider,
    Chip,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useState, useEffect } from "react";
import { toast, Toaster } from "react-hot-toast";
import * as services from "../../services/services";
import { syncPetHistoryToBow } from "../../utils/syncPetHistory";
import IMG from "../../images/istockphoto.jpg";

const ConnectionRequestDetail = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { id } = useParams();
    // Expecting the request object to be passed via routing state
    const initialRequest = location.state?.request || null;
    const [request, setRequest] = useState(initialRequest);
    const [hospitalPetFull, setHospitalPetFull] = useState(null);
    const [ownerPetFull, setOwnerPetFull] = useState(null);
    const [loadingAction, setLoadingAction] = useState(false);
    const [loadingData, setLoadingData] = useState(true);
    const [error, setError] = useState(null);
    const [isSynced, setIsSynced] = useState(false);

    useEffect(() => {
        const fetchAllData = async () => {
            setLoadingData(true);
            try {
                let currentRequest = initialRequest;

                // 1. Fetch Request Link Data if not provided via navigation state
                if (!currentRequest) {

                    const res = await services.getPetLinks();

                    const rawData = res.data || [];
                    currentRequest = rawData.find(item => String(item.id) === String(id));

                    if (!currentRequest) {
                        setError("ไม่พบข้อมูลคำขอ");
                        setLoadingData(false);
                        return;
                    }
                    setRequest(currentRequest);
                }

                const localPetId = currentRequest?.petId
                console.log("localPetId", initialRequest);
                if (localPetId) {
                    try {
                        const hPetRes = await services.getPetById(localPetId);
                        setHospitalPetFull(hPetRes.data.data);
                    } catch (e) {
                        console.error("Failed to fetch hospital pet detail", e);
                    }
                }

                // 3. Fetch Full Owner (Bow) Pet Data
                const bowPetId = currentRequest.ownerPetId;
                if (bowPetId) {
                    try {
                        const bPetRes = await services.getPetBowById(bowPetId);
                        setOwnerPetFull(bPetRes.data);
                    } catch (e) {
                        console.error("Failed to fetch bow pet detail", e);
                    }
                }

            } catch (err) {
                console.error("Failed to fetch request detail", err);
                setError("เกิดข้อผิดพลาดในการโหลดข้อมูล");
            } finally {
                setLoadingData(false);
            }
        };

        fetchAllData();
    }, [id, initialRequest]);

    if (loadingData) {
        return (
            <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="h6">กำลังโหลดข้อมูล...</Typography>
            </Box>
        );
    }

    // If no request data is found (e.g., direct URL access without state), go back
    if (error || !request) {
        return (
            <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="h6">{error || "ไม่พบข้อมูลคำขอ"}</Typography>
                <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
                    กลับไปหน้าก่อนหน้า
                </Button>
            </Box>
        );
    }

    const { status } = request;
    const requestId = request.id;

    const handleAction = async (newStatus) => {
        setLoadingAction(true);
        try {
            await services.updatePetLinkStatus(requestId);
            toast.success(newStatus === "LINKED" ? "ยอมรับคำขอเรียบร้อย" : "ปฏิเสธคำขอเรียบร้อย");
            setRequest(prev => ({ ...prev, status: newStatus }));

            // Sync historical records if LINKED
            if (newStatus === "LINKED" && hospitalPetFull?.id && request?.ownerPetId) {
                await syncPetHistoryToBow(hospitalPetFull.id, request.ownerPetId);
                setIsSynced(true);
            }
        } catch (error) {
            console.error("Failed to update status", error);
            toast.error("ทำรายการไม่สำเร็จ");
        } finally {
            setLoadingAction(false);
        }
    };

    const handleSyncManual = async () => {
        if (hospitalPetFull?.id && request?.ownerPetId) {
            await syncPetHistoryToBow(hospitalPetFull.id, request.ownerPetId);
            setIsSynced(true);
        }
    };

    const PetCard = ({ pet, isHospital }) => {
        if (!pet) return null;

        // Determine owner name based on the structure (hospital pet vs bow pet)
        let ownerDisplayName = "ไม่ระบุ";
        if (isHospital) {
            if (pet.owner) {
                ownerDisplayName = `${pet.owner.firstName || ""} ${pet.owner.lastName || ""}`.trim();
            } else if (pet.ownerName) {
                ownerDisplayName = pet.ownerName;
            }
        } else {
            // Bow Pet structure usually has 'owners' array
            if (pet.owners && pet.owners.length > 0) {
                ownerDisplayName = `${pet.owners[0].first_name || ""} ${pet.owners[0].last_name || ""}`.trim();
            } else if (pet.owner) { // fallback just in case
                ownerDisplayName = `${pet.owner.firstName || pet.owner.first_name || ""} ${pet.owner.lastName || pet.owner.last_name || ""}`.trim();
            }
        }

        return (
            <Paper sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
                <Typography variant="h6" align="center" gutterBottom color="primary" fontWeight="bold">
                    {isHospital ? "ข้อมูลฝั่งโรงพยาบาล" : "ข้อมูลฝั่งเจ้าของ"}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Avatar
                        src={pet?.image || IMG}
                        variant="rounded"
                        sx={{ width: 100, height: 100 }}
                    />
                    <Box>
                        <Typography variant="h6" fontWeight="bold">
                            {pet?.name || "-"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {pet?.type || "-"} | {pet?.breed || "-"}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                            Pet ID : <b>{pet?.hospitalPetCode || pet?.id || "-"}</b>
                        </Typography>
                        <Typography variant="caption" display="block">
                            Microchip ID : <b>{pet?.microchipNumber || pet?.microchip_number || "-"}</b>
                        </Typography>
                    </Box>
                </Box>
                <Divider sx={{ my: 'auto', py: 1 }} />
                <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        เจ้าของ
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                        {ownerDisplayName || "ไม่ระบุ"}
                    </Typography>
                </Box>
            </Paper>
        );
    };

    // Determine the data to display: prefer the full fetched data, fallback to the summary data in the request
    const displayHospitalPet = hospitalPetFull || request.hospitalPet || request.pet;
    const displayOwnerPet = ownerPetFull || request.ownerPet;

    return (
        <>
            <Toaster />
            <AppBreadcrumbs />
            <Box sx={{ p: { xs: 1, sm: 3 }, maxWidth: 1000, mx: "auto" }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mb: 4 }}>
                    {/* Hospital Pet Detail */}
                    <Box sx={{ flex: 1 }}>
                        {displayHospitalPet ? (
                            <PetCard pet={displayHospitalPet} isHospital={true} />
                        ) : (
                            <Typography color="text.secondary" align="center">กำลังโหลดข้อมูลสัตว์เลี้ยงโรงพยาบาล...</Typography>
                        )}
                    </Box>

                    {/* Owner Pet Detail */}
                    <Box sx={{ flex: 1 }}>
                        {displayOwnerPet ? (
                            <PetCard pet={displayOwnerPet} isHospital={false} />
                        ) : (
                            <Typography color="text.secondary" align="center">กำลังโหลดข้อมูลสัตว์เลี้ยงเจ้าของ...</Typography>
                        )}
                    </Box>
                </Box>

                {/* Action Area */}
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, p: 2 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                        {status === "PENDING" ? (
                            request.requestedBy === "HOSPITAL" ? (
                                <Chip
                                    label="กำลังรอ Owner ดำเนินการ"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ fontSize: "1.1rem", p: 2, fontWeight: "bold" }}
                                />
                            ) : (
                                <>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        size="large"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={() => handleAction("LINKED")}
                                        disabled={loadingAction}
                                        sx={{ px: 4, py: 1.5, fontSize: "1.1rem" }}
                                    >
                                        ยอมรับ
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        size="large"
                                        startIcon={<CancelIcon />}
                                        onClick={() => handleAction("REJECTED")}
                                        disabled={loadingAction}
                                        sx={{ px: 4, py: 1.5, fontSize: "1.1rem", borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                                    >
                                        ปฏิเสธ
                                    </Button>
                                </>
                            )
                        ) : (
                            <Chip
                                label={status === "LINKED" ? "สถานะ: ยอมรับแล้ว" : "สถานะ: ปฏิเสธแล้ว"}
                                color={status === "LINKED" ? "success" : "error"}
                                sx={{ fontSize: "1.1rem", p: 2 }}
                            />
                        )}
                    </Box>

                    {status === "LINKED" && (
                        <Button
                            variant="contained"
                            color="info"
                            startIcon={<CloudUploadIcon />}
                            onClick={handleSyncManual}
                            disabled={isSynced}
                            sx={{ mt: 1, px: 3 }}
                        >
                            {isSynced ? "ซิงก์ข้อมูลประวัติเรียบร้อยแล้ว" : "ซิงก์ประวัติย้อนหลังไป Bow"}
                        </Button>
                    )}
                </Box>
            </Box>
        </>
    );
};

export default ConnectionRequestDetail;
